import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { forkJoin } from 'rxjs';
import { BsModalService } from 'ngx-bootstrap/modal';
import { OrganisationService } from '../../services/organisation/organisation.service';
import { BillingModalComponent } from '../../components/billing-modal/billing-modal.component';
import { CHARGEBEE_PLANS, ORG_SIZE_MAPPING, PLAN_MAPPING } from '../../components/billing/billing.types';
import {ConstantsService} from "../../services/constants/constants.service";
import {PermissionsService} from "../../services/permissions/permissions.service";

interface Plan {
  name: string;
  cbName: string;
  uid: string;
  description: string;
  monthlyPrice: number;
  annualPrice: number;
  isRecommended?: boolean;
  isEnterprise?: boolean;
}

const PLAN_ORDER = ['SS_Starter', 'SS_Growth', 'SS_Plus', 'SS_Premium'];

const IFRAME_HEIGHT_BUFFER_PX = 120;

const ENTERPRISE_PLAN: Plan = {
  name: 'Xref Enterprise',
  cbName: '',
  uid: '',
  description:
    'Contact our sales team about an Xref Enterprise plan. Perfect for large organisations with over 500 employees.',
  monthlyPrice: 0,
  annualPrice: 0,
  isEnterprise: true,
};

@Component({
  selector: 'app-plan-comparison',
  templateUrl: './plan-comparison.component.pug',
  styleUrls: ['./plan-comparison.component.scss'],
  standalone: false,
})
export class PlanComparisonComponent implements OnInit, OnDestroy {
  selectedCurrency = 'USD';
  currencies = ['USD', 'AUD', 'NZD'];
  isLoading = true;
  iframeHeightPx = 2100;

  private apiPlans: any[] = [];
  private recommendedPlanId = '';
  private onMessageListener?: (event: MessageEvent) => void;
  private iframeResizeObserver?: ResizeObserver;
  private iframeMeasureTimeouts: ReturnType<typeof setTimeout>[] = [];

  @ViewChild('planComparisonIframe', { static: false })
  private planComparisonIframe?: ElementRef<HTMLIFrameElement>;

  plans: Plan[] = [];
  isRedirectFlow = false;
  constructor(
    private organisationService: OrganisationService,
    private modalService: BsModalService,
    private permissionsService: PermissionsService,
  ) {}

  ngOnInit(): void {
    this.isRedirectFlow = this.permissionsService.redirectToPlanComparison;
    this.loadPlans();

    const embedOrigins = [window.location.origin, 'https://www.xref.com', 'https://xref.com'];
    this.onMessageListener = (event: MessageEvent) => {
      if (!embedOrigins.includes(event.origin)) {
        return;
      }

      let data: any = event.data;
      if (typeof data === 'string') {
        try {
          data = JSON.parse(data);
        } catch {
          return;
        }
      }
      if (!data || data.type !== 'xref:embed:height') {
        return;
      }

      const nextHeight = Number(data.height);
      if (!Number.isFinite(nextHeight) || nextHeight <= 0) {
        return;
      }

      this.applyIframeContentHeight(nextHeight);
    };

    window.addEventListener('message', this.onMessageListener);
  }

  private loadPlans(): void {
    const validPlanIds = Object.values(CHARGEBEE_PLANS);

    forkJoin({
      org: this.organisationService.getOrganisation(),
      plans: this.organisationService.getCbPlanItems(),
    }).subscribe({
      next: ({ org, plans }) => {
        const orgSizeKey = ORG_SIZE_MAPPING[org?.org_size as keyof typeof ORG_SIZE_MAPPING];
        if (orgSizeKey) {
          this.recommendedPlanId = PLAN_MAPPING[orgSizeKey as keyof typeof PLAN_MAPPING];
        }

        this.apiPlans = (plans || [])
          .filter((plan: any) => plan.cb_id && validPlanIds.includes(plan.cb_id))
          .sort((a: any, b: any) => PLAN_ORDER.indexOf(a.cb_id) - PLAN_ORDER.indexOf(b.cb_id))
          .map((plan: any) => {
            plan.cb_name = plan.cb_id.replace('SS_', '');
            return plan;
          });

        this.buildPlans();
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading plans:', error);
        this.isLoading = false;
      },
    });
  }

  private buildPlans(): void {
    this.plans = this.apiPlans.map((apiPlan) => {
      const profileCap = apiPlan.cb_meta?.profile_cap || '0';

      const prices = (apiPlan.item_prices || []).filter((p: any) => p.currency_display === this.selectedCurrency);
      const monthlyPrice = prices.find((p: any) => p.frequency_display === 'monthly')?.price || 0;
      const yearlyTotal = prices.find((p: any) => p.frequency_display === 'yearly')?.price || 0;

      return {
        name: `Xref ${apiPlan.cb_name}`,
        cbName: apiPlan.cb_name,
        uid: apiPlan.uid,
        description: `For organisations with a typical employee turnover of up to ${profileCap} per year.`,
        monthlyPrice,
        annualPrice: Math.round((yearlyTotal / 12) * 100) / 100,
        isRecommended: apiPlan.cb_id === this.recommendedPlanId,
      } as Plan;
    });

    this.plans.push({ ...ENTERPRISE_PLAN });

    if (!this.plans.some(p => p.isRecommended)) {
      const enterprise = this.plans.find(p => p.isEnterprise);
      if (enterprise) {
        enterprise.isRecommended = true;
      }
    }
  }

  onIframeLoad(): void {
    this.tryAutoResizeIframe();
  }

  private applyIframeContentHeight(contentHeight: number): void {
    this.iframeHeightPx = Math.max(1000, Math.ceil(contentHeight) + IFRAME_HEIGHT_BUFFER_PX);
  }

  private clearIframeMeasureTimeouts(): void {
    this.iframeMeasureTimeouts.forEach((id) => clearTimeout(id));
    this.iframeMeasureTimeouts = [];
  }

  private tryAutoResizeIframe(): void {
    const iframeEl = this.planComparisonIframe?.nativeElement;
    if (!iframeEl) {
      return;
    }

    this.clearIframeMeasureTimeouts();

    try {
      const doc = iframeEl.contentDocument || iframeEl.contentWindow?.document;
      if (!doc) {
        return;
      }

      const measure = () => {
        const nextHeight = doc.documentElement?.scrollHeight ?? doc.body?.scrollHeight;
        if (!nextHeight) return;
        this.applyIframeContentHeight(nextHeight);
      };

      measure();
      [250, 1000, 2000, 3500, 5000].forEach((ms) => {
        const id = setTimeout(() => measure(), ms);
        this.iframeMeasureTimeouts.push(id);
      });

      this.iframeResizeObserver?.disconnect();
      this.iframeResizeObserver = new ResizeObserver(() => measure());
      this.iframeResizeObserver.observe(doc.documentElement);
    } catch {
      return;
    }
  }

  ngOnDestroy(): void {
    if (this.onMessageListener) {
      window.removeEventListener('message', this.onMessageListener);
    }
    this.clearIframeMeasureTimeouts();
    this.iframeResizeObserver?.disconnect();
  }

  selectCurrency(currency: string): void {
    this.selectedCurrency = currency;
    this.buildPlans();
  }

  activateSubscription(plan: Plan): void {
    const apiPlan = this.apiPlans.find((p) => p.uid === plan.uid);
    const prices = apiPlan?.item_prices || [];
    const yearlyPrice = prices.find(
      (p: any) => p.currency_display === this.selectedCurrency && p.frequency_display === 'yearly',
    );

    this.modalService.show(BillingModalComponent, {
      class: 'modal-dialog-centered modal-lg modal-dialog-rounded',
      initialState: {
        preSelectedPlan: plan.cbName,
        initialCurrency: this.selectedCurrency,
        planUid: plan.uid,
        cbItemPriceId: yearlyPrice?.cb_item_price_id || '',
        prices: prices,
      },
    });
  }

  contactSales(): void {
    window.open(ConstantsService.SIGNUP_CONTACT_XREF_URL_ENT, '_blank', 'noopener,noreferrer');
  }
}
